import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import { FLAGS } from "@/components/LanguageSwitcher";
import { RIVIERA_CITIES, NL_AREAS } from "@/lib/riviera";
import { isRivieraPath } from "@/components/CountrySwitcher";
import { FR_ENTITY, FR_LEGAL_LINKS } from "@/lib/legalFr";
import { openCookieSettings } from "@/components/CookieConsent";

const H4 = "text-white text-xs font-bold mb-4 tracking-[0.2em] uppercase";
const SMALL = "text-slate-400 hover:text-white transition-colors";

export default function PublicFooter() {
  const { t } = useTranslation();
  const riviera = isRivieraPath(useLocation().pathname);
  const legal = riviera ? FR_LEGAL_LINKS : [["/terms", t("footer.terms")], ["/privacy", t("footer.privacy")]];
  return (
    <footer className="mt-24 bg-[#0F172A] text-slate-300" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-14 pr-16 sm:pr-20 md:pr-20 xl:pr-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg p-1.5 inline-block"><Logo className="h-7" /></div>
          <p className="mt-5 text-sm text-slate-300 max-w-sm leading-relaxed">{t("footer_v3.tagline")}</p>
          <address className="mt-6 not-italic text-xs text-slate-400 leading-relaxed space-y-0.5" data-testid="footer-business-info">
            {riviera ? (
              <div data-testid="footer-fr-entity">
                <div className="text-slate-200 font-semibold">TRUSTLINE SERVICES FRANCE</div>
                <div>SIREN : {FR_ENTITY.siren} · SIRET : {FR_ENTITY.siret}</div>
                <div>{FR_ENTITY.address}</div>
                <div><a href={`mailto:${FR_ENTITY.email}`} className="hover:text-white">{FR_ENTITY.email}</a></div>
                <div><a href="https://wa.me/31642449019" className="hover:text-white">WhatsApp {FR_ENTITY.whatsapp}</a></div>
              </div>
            ) : (
              <div data-testid="footer-nl-entity">
                <div className="text-slate-200 font-semibold">Trustline Schoonmaken</div>
                <div>Julianastraat 52B · 2405 CJ Alphen aan den Rijn</div>
                <div><a href="tel:+31642449019" className="hover:text-white">+31 6 4244 9019</a></div>
                <div><a href="mailto:info@trustlineservices.nl" className="hover:text-white">info@trustlineservices.nl</a></div>
                <div>KvK: 99642441 · VAT: NL004861783B47</div>
              </div>
            )}
          </address>
        </div>
        <div className="lg:col-span-3 space-y-6" data-testid="footer-markets">
          <h4 className={`${H4} !mb-0`}>{t("footer_v3.markets")}</h4>
          <Market flag="NL" title={t("footer_v3.nl_title")} to="/" testid="footer-market-nl">
            {NL_AREAS.map((a) => <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-slate-400 hover:text-[#9BD478]" data-testid={`footer-nl-city-${a.slug}`}>{a.city}</Link>)}
          </Market>
          <Market flag="FR" title={t("footer_v3.fr_title")} to="/fr" testid="footer-market-fr">
            {RIVIERA_CITIES.map((c) => <Link key={c.slug} to={`/fr/${c.slug}`} className="text-slate-400 hover:text-sky-300" data-testid={`footer-city-${c.slug}`}>{c.name}{c.inquiryOnly ? "*" : ""}</Link>)}
          </Market>
          <p className="ml-9 text-xs text-amber-200/90" data-testid="footer-monaco-note">{t("footer_v3.monaco_note")}</p>
        </div>
        <div className="md:col-span-2 lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-8">
          <FooterCol title={t("footer.company")} testid="footer-company" links={[
            ["/about", t("nav.about")], [riviera ? "/fr#services" : "/services", t("nav.services")],
            ...(riviera ? [] : [["/service-areas", t("nav_v3.locations")]]), ["/contact", t("nav.contact")],
          ]} />
          <FooterCol title={t("footer_v3.resources")} testid="footer-resources" links={[
            ["/checklist", "Checklist"], ["/blog", t("nav.blog")], [riviera ? "/faq?country=fr" : "/faq", t("nav.faq")],
          ]} />
          <FooterCol title={t("footer.account")} testid="footer-account" links={[
            [riviera ? "/fr/book" : "/book", t("footer.book_online")], [riviera ? "/fr/inquiry" : "/quote", t("footer.request_quote")],
            ["/login", t("nav_v3.manage_booking")],
          ]} />
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 text-xs text-slate-400 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
          <p className="shrink-0">© 2026 Trustline Services. All rights reserved.</p>
          <nav className="flex flex-wrap gap-x-4 gap-y-2 lg:flex-1" data-testid={riviera ? "footer-fr-legal" : "footer-nl-legal"} lang={riviera ? "fr" : undefined}>
            {legal.map(([to, l]) => <Link key={to} to={to} className={SMALL} data-testid={`footer-legal-${to.replace(/\//g, "-").replace(/^-/, "")}`}>{l}</Link>)}
            <button type="button" onClick={openCookieSettings} className={SMALL} data-testid="footer-cookie-settings">{t("cookies.settings")}</button>
          </nav>
          <Link to="/login" className="shrink-0 text-slate-500 hover:text-slate-300 transition-colors" data-testid="footer-admin-login">{t("footer.admin_login")}</Link>
        </div>
      </div>
    </footer>
  );
}

function Market({ flag, title, to, testid, children }) {
  return (
    <div>
      <Link to={to} className="flex gap-3 items-center group" data-testid={testid}>
        <span className="block w-6 h-6 rounded-full overflow-hidden ring-1 ring-white/20 shrink-0">{FLAGS[flag]}</span>
        <span className="text-sm font-semibold text-white group-hover:text-[#9BD478] transition-colors">{title}</span>
      </Link>
      <div className="mt-2 ml-9 flex flex-wrap gap-x-3 gap-y-1.5 text-sm">{children}</div>
    </div>
  );
}

function FooterCol({ title, links, testid }) {
  return (
    <div data-testid={testid}>
      <h4 className={H4}>{title}</h4>
      <ul className="space-y-2.5 text-sm">
        {links.map(([to, label]) => <li key={to}><Link className="text-slate-300 hover:text-white transition-colors" to={to} data-testid={`footer-link-${to.split("?")[0].replace(/\//g, "-").replace(/^-/, "")}`}>{label}</Link></li>)}
      </ul>
    </div>
  );
}
